import { useEffect } from "react";
import Lenis from "lenis";

export default function useLenis() {
    useEffect(() => {
        const lenis = new Lenis({
            duration: 1.15,
            easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
            smoothWheel: true,
            smoothTouch: false,
            touchMultiplier: 1.4,
        });
        // Expose for PageShell / BackToTop to trigger programmatic smooth scroll.
        window.__lenis = lenis;

        // Track the pending frame (not just the first one) and hard-stop on
        // teardown — otherwise the loop outlives the instance it drives and the
        // surviving window.__lenis can end up with no rAF pump at all.
        //
        // `__driven` records that the pump has actually ticked. Lenis virtualises
        // scrolling, so an instance that is never ticked silently swallows every
        // scrollTo; callers check this flag before handing it a scroll.
        let stopped = false;
        let rafId = requestAnimationFrame(function loop(time) {
            if (stopped) return;
            lenis.__driven = true;
            lenis.raf(time);
            rafId = requestAnimationFrame(loop);
        });

        // anchor scroll delegate — captures every internal #anchor click.
        // If Lenis is not actually driving, we leave the event alone so the
        // browser performs its own native jump rather than nothing at all.
        const onAnchorClick = (e) => {
            if (!lenis.__driven) return;
            const a = e.target.closest("a[href^='#']");
            if (!a) return;
            const href = a.getAttribute("href");
            if (!href || href === "#") return;
            const el = document.querySelector(href);
            if (!el) return;
            e.preventDefault();
            lenis.scrollTo(el, { offset: -8, duration: 1.25 });
        };
        document.addEventListener("click", onAnchorClick);

        return () => {
            stopped = true;
            cancelAnimationFrame(rafId);
            document.removeEventListener("click", onAnchorClick);
            if (window.__lenis === lenis) delete window.__lenis;
            lenis.destroy();
        };
    }, []);
}
